// Broken out of the country-dropdown.tsx file.
// This is a generic dropdown component that can be used in the app.

import React, { useCallback, useState, forwardRef, useEffect } from 'react';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption<T = unknown> {
  label: string;
  value: string;
  data?: T;
}

interface DropdownProps<T = unknown> {
  options: DropdownOption<T>[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  slim?: boolean;
  renderOption?: (option: DropdownOption<T>) => React.ReactNode;
  renderTrigger?: (selected?: DropdownOption<T>) => React.ReactNode;
  searchPlaceholder?: string;
  showSearch?: boolean;
}

const DropdownComponent = <T = unknown,>(
  {
    options,
    value,
    onChange,
    placeholder = 'Select an option',
    disabled = false,
    slim = false,
    renderOption,
    renderTrigger,
    searchPlaceholder = 'Search...',
    showSearch = false,
    ...props
  }: DropdownProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<DropdownOption<T> | undefined>(
    undefined
  );

  useEffect(() => {
    if (value) {
      const found = options.find(opt => opt.value === value);
      setSelected(found);
    } else {
      setSelected(undefined);
    }
  }, [value, options]);

  const handleSelect = useCallback(
    (option: DropdownOption<T>) => {
      setSelected(option);
      onChange?.(option.value);
      setOpen(false);
    },
    [onChange]
  );

  const triggerClasses = cn(
    // Layout
    'flex h-9 w-full items-center justify-between',
    // Typography
    'text-sm whitespace-nowrap',
    // Background & Border
    'bg-transparent rounded-md border border-input',
    // Spacing
    'px-3 py-2',
    // States
    'outline-none disabled:cursor-not-allowed disabled:opacity-50',
    // Focus States
    'focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500',
    // Effects
    'shadow-xs transition-[color,box-shadow]',
    // Placeholder
    'placeholder:text-muted-foreground',
    // Text Overflow
    '[&>span]:line-clamp-1',
    // Cursor
    'cursor-pointer',
    // Conditional
    slim === true && 'w-20'
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={ref}
        className={triggerClasses}
        disabled={disabled}
        {...props}
      >
        {renderTrigger ? (
          renderTrigger(selected)
        ) : (
          <span>{selected ? selected.label : placeholder}</span>
        )}
        <ChevronDown size={16} />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={10}
        side="bottom"
        className="min-w-[--radix-popper-anchor-width] p-0 bg-white"
      >
        <Command className="w-full max-h-[200px] sm:max-h-[270px]">
          <CommandList>
            {showSearch ? (
              <div className="sticky top-0 z-10 bg-popover bg-white">
                <CommandInput placeholder={searchPlaceholder} />
              </div>
            ) : (
              // This is needed for accessibility.
              // You're able to use the keyboard to navigate the options.
              <div className="sr-only">
                <CommandInput />
              </div>
            )}
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option, key) => (
                <CommandItem
                  className={cn(
                    'flex items-center w-full gap-2',
                    'space-x-2',
                    'overflow-hidden',
                    'cursor-pointer',
                    'transition-colors',
                    value === option.value && 'font-semibold'
                  )}
                  key={key}
                  onSelect={() => handleSelect(option)}
                >
                  {renderOption ? (
                    renderOption(option)
                  ) : (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {option.label}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

DropdownComponent.displayName = 'DropdownComponent';

export const Dropdown = forwardRef(DropdownComponent) as <T = unknown>(
  props: DropdownProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => React.ReactElement;
